import { useEffect } from "react";
import { DeviceUUID } from "device-uuid";

import { Language, LanguageService, User } from "../client/api";
import { browserLang, getDeviceInfo } from "../config/utils";
import { AS_LANG } from "../config/constants";
import { markNewUserNeedsAoiSelection } from "./useAoiSelector";

type UseUserStatusEffectParams = {
  status: string;
  error?: string | null;
  aoi?: { id: number } | null;
  languageObject?: Language | null;
  user?: User | null;
  fbToken: string | null;
  createUser: (payload: {
    deviceId: string;
    deviceInfo: string;
    areaOfInterestId: number;
    languageId: number;
    firebaseToken?: string;
  }) => void;
  refreshToken: () => void;
};

export const resolveLanguageId = async (languageObject?: Language | null) => {
  if (languageObject?.id) return languageObject.id;

  const stored = window.localStorage.getItem(AS_LANG);
  if (stored && /^\d+$/.test(stored)) {
    return parseInt(stored, 10);
  }

  try {
    const list = await LanguageService.getLanguagesLanguageGet();
    const byLocale = list.find((l) => l.locale === browserLang());
    return byLocale?.id ?? list[0]?.id ?? 1;
  } catch (_) {
    return 1;
  }
};

export const createUserWithResolvedLanguage = async ({
  languageObject,
  areaOfInterestId,
  fbToken,
  createUser,
  isFirstOpen = false,
}: {
  languageObject?: Language | null;
  areaOfInterestId?: number;
  fbToken: string | null;
  createUser: UseUserStatusEffectParams["createUser"];
  isFirstOpen?: boolean;
}) => {
  if (isFirstOpen) {
    markNewUserNeedsAoiSelection();
  }

  const languageId = await resolveLanguageId(languageObject);

  createUser({
    deviceId: new DeviceUUID().get(),
    deviceInfo: JSON.stringify(getDeviceInfo()),
    areaOfInterestId: areaOfInterestId ?? 1,
    languageId,
    firebaseToken: fbToken ?? undefined,
  });
};

export const useUserStatusEffect = ({
  status,
  error,
  aoi,
  languageObject,
  user,
  fbToken,
  createUser,
  refreshToken,
}: UseUserStatusEffectParams) => {
  useEffect(() => {
    if (status === "failed") {
      if (error === "not_registered" || error === "first_open") {
        createUserWithResolvedLanguage({
          languageObject,
          areaOfInterestId: aoi?.id,
          fbToken,
          createUser,
          isFirstOpen: error === "first_open",
        }).catch(() => undefined);
      } else if (error === "no_user_found") {
        refreshToken();
      }
    } else if (status !== "loading" && aoi && languageObject && !user) {
      refreshToken();
    }
  }, [
    status,
    error,
    aoi,
    languageObject,
    user,
    fbToken,
    createUser,
    refreshToken,
  ]);
};

